import { useCallback, useMemo, useEffect, useRef } from 'react';
import {
  OnboardingBaseTask,
  OnboardingStatus,
  TaskStatus,
} from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { ProductTier } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/product.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import dayjs from 'dayjs';
import { useGetUserOnboardingStatusQuery, useUpdateOnboardingStatusMutation } from '@frontend/api-call-intel';
import { useHasCallIntelAclAccess } from '@frontend/call-intel-core';
import { useAppScopeStore } from '@frontend/scope';
import { usePreference } from '@frontend/user-preferences';
import { CI_BASE_CHECKLIST_POLICY } from '../constant';
import { buildCIBaseOnboardingFlow } from '../flow-descriptor';
import { CI_BASE_ONBOARDING_EVENTS, trackOnboardingEvent } from '../tracking';
import { getLatestCompletedAt, getCurrentTimestamp, hasDaysElapsed } from '../utils/onboarding-lifecycle';

interface BaseTaskProgress {
  baseTask: OnboardingBaseTask;
  status: TaskStatus;
  completedAt?: string;
}

type PendingTaskCompletionMetadata = {
  taskId: string;
  taskLabel?: string;
  completedEvent?: string;
  isAllCompletedAfterThisTask: boolean;
};

type UseBaseOnboardingProgressArgs = {
  /**
   * Controls whether this hook should call GetUserOnboardingStatus.
   *
   * Useful for avoiding the onboarding status API call until basic eligibility
   * gates have passed.
   */
  enabled?: boolean;

  /**
   * Allows this hook call to write lifecycle timestamps.
   *
   * This hook is used in multiple places:
   *
   * - useOnboardingEligibility:
   *   Reads hidden/status only. Should NOT start timers.
   *
   * - GetStartedContent / BaseProductTaskContent:
   *   Reads completed task groups for UI. Should NOT start timers.
   *
   * - CIBaseOnboarding:
   *   Registers the onboarding flow when the user is eligible.
   *   This is the only place that should start/update lifecycle timers.
   *
   * Without this flag, simply rendering a badge/checklist or checking eligibility
   * could set firstSeenAt too early and expire onboarding before the user actually
   * had access to it.
   */
  trackLifecycle?: boolean;
};

const ONBOARDING_STATUS_STALE_TIME = 1000 * 60 * 5;

export function useBaseOnboardingProgress({
  enabled = true,
  trackLifecycle = false,
}: UseBaseOnboardingProgressArgs = {}) {
  const [lifecycle, setLifecycle] = usePreference('CIBaseOnboardingLifecycle');
  const expiryTrackedRef = useRef(false);
  const pendingTaskCompletionMetadataRef = useRef(new Map<OnboardingBaseTask, PendingTaskCompletionMetadata>());

  const { selectedLocationIds: appScopeLocationIds } = useAppScopeStore();

  const isPermanentlyHidden = !!lifecycle.hiddenAt;

  const {
    data: onboardingData,
    isLoading: queryLoading,
    isError: hasOnboardingStatusError,
  } = useGetUserOnboardingStatusQuery({
    request: { productTier: ProductTier.PRODUCT_TIER_BASE, locationIds: appScopeLocationIds },
    options: {
      staleTime: ONBOARDING_STATUS_STALE_TIME,
      refetchOnMount: false,
      enabled: enabled && !isPermanentlyHidden && !!appScopeLocationIds.length,
      retry: false,
    },
  });

  const isLoading = enabled && !isPermanentlyHidden && !hasOnboardingStatusError ? queryLoading : false;

  const canAssignUserRoles = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_READ, Permission.USER_WRITE], {
    allowDemoOverride: false,
    requireAllPermissions: true,
  });

  const flow = useMemo(() => buildCIBaseOnboardingFlow({ canAssignUserRoles }), [canAssignUserRoles]);

  const onboardingStatus = onboardingData?.status;

  const tasks = useMemo(() => (onboardingData?.tasks ?? []) as BaseTaskProgress[], [onboardingData?.tasks]);

  const completedGroups = useMemo(() => {
    if (onboardingStatus === OnboardingStatus.ONBOARDING_STATUS_COMPLETED) {
      return flow.tasks.map((task) => task.id as OnboardingBaseTask);
    }

    return tasks.filter((task) => task.status === TaskStatus.TASK_STATUS_COMPLETED).map((task) => task.baseTask);
  }, [onboardingStatus, tasks, flow.tasks]);

  const isAllCompleted = useMemo(() => {
    if (flow.tasks.length === 0) return false;

    return flow.tasks.every((task) => completedGroups.includes(task.id as OnboardingBaseTask));
  }, [flow.tasks, completedGroups]);

  const latestTaskCompletedAtFromApi = useMemo(
    () =>
      getLatestCompletedAt(
        tasks.filter((task) => task.status === TaskStatus.TASK_STATUS_COMPLETED).map((task) => task.completedAt)
      ),
    [tasks]
  );

  const finalCompletedAtFromApi = latestTaskCompletedAtFromApi ?? lifecycle.lastTaskCompletedAt;

  const { mutate: updateStatus } = useUpdateOnboardingStatusMutation({
    options: {
      onSuccess: (_response, request) => {
        const baseTask = request.task && 'baseTask' in request.task ? request.task.baseTask : undefined;
        const metadata = baseTask ? pendingTaskCompletionMetadataRef.current.get(baseTask) : undefined;

        if (metadata?.completedEvent) {
          trackOnboardingEvent(metadata.completedEvent, {
            taskId: metadata.taskId,
            taskLabel: metadata.taskLabel ?? null,
          });
        }

        if (metadata?.isAllCompletedAfterThisTask) {
          trackOnboardingEvent(CI_BASE_ONBOARDING_EVENTS.checklistCompleted);
        }

        if (baseTask) {
          pendingTaskCompletionMetadataRef.current.delete(baseTask);
        }
      },

      onError: (_error, request) => {
        const baseTask = request.task && 'baseTask' in request.task ? request.task.baseTask : undefined;

        if (baseTask) {
          pendingTaskCompletionMetadataRef.current.delete(baseTask);
        }
      },
    },
  });

  /**
   * Sync Base Onboarding lifecycle timestamps.
   *
   * Writes:
   *
   * 1. firstSeenAt
   *    - set once when checklist is actually eligible/registered
   *    - used as 30-day expiry anchor if no task has been completed
   *
   * 2. lastTaskCompletedAt
   *    - synced from BE task completedAt
   *    - resets the 30-day incomplete expiry timer
   *
   * 3. completedAt
   *    - set when all checklist tasks are completed
   *    - starts the 14-day completed retention window
   */
  useEffect(() => {
    if (!trackLifecycle) return;
    if (isLoading || isPermanentlyHidden || hasOnboardingStatusError || !onboardingData) return;

    const nextLifecycle = { ...lifecycle };
    let changed = false;

    if (!nextLifecycle.firstSeenAt) {
      nextLifecycle.firstSeenAt = getCurrentTimestamp();
      changed = true;
    }

    const hasNewerTaskCompletedAt =
      latestTaskCompletedAtFromApi &&
      (!nextLifecycle.lastTaskCompletedAt ||
        dayjs(latestTaskCompletedAtFromApi).isAfter(dayjs(nextLifecycle.lastTaskCompletedAt)));

    if (hasNewerTaskCompletedAt) {
      nextLifecycle.lastTaskCompletedAt = latestTaskCompletedAtFromApi;
      changed = true;
    }

    if (isAllCompleted && !nextLifecycle.completedAt && finalCompletedAtFromApi) {
      nextLifecycle.completedAt = finalCompletedAtFromApi;
      changed = true;
    }

    if (changed) {
      setLifecycle(nextLifecycle);
    }
  }, [
    trackLifecycle,
    isLoading,
    isPermanentlyHidden,
    onboardingData,
    lifecycle,
    latestTaskCompletedAtFromApi,
    finalCompletedAtFromApi,
    isAllCompleted,
    setLifecycle,
    hasOnboardingStatusError,
  ]);

  const shouldExpireCompletedChecklist =
    isAllCompleted && hasDaysElapsed(lifecycle.completedAt, CI_BASE_CHECKLIST_POLICY.completedRetentionDays);

  const incompleteExpiryAnchor = lifecycle.lastTaskCompletedAt ?? lifecycle.firstSeenAt;

  const shouldExpireIncompleteChecklist =
    !isAllCompleted && hasDaysElapsed(incompleteExpiryAnchor, CI_BASE_CHECKLIST_POLICY.incompleteExpiryDays);

  /**
   * Permanently hide the checklist once a PRD expiry condition is met.
   *
   * Two removal cases:
   *
   * 1. completed-expired
   *    - all tasks are completed
   *    - 14 days have passed since `completedAt`
   *
   * 2. incomplete-expired
   *    - checklist is not fully completed
   *    - 30 days have passed since the latest task activity
   *    - if no task was ever completed, count from `firstSeenAt`
   *
   * We do not call `updateStatus(ONBOARDING_STATUS_EXPIRED)` here because the
   * API mutation requires a `task`. Sending a fake task would be incorrect.
   * The lifecycle preference is the FE source of truth for PRD-based UI removal.
   */
  useEffect(() => {
    if (!trackLifecycle) return;
    if (isLoading || isPermanentlyHidden || hasOnboardingStatusError) return;
    if (!shouldExpireCompletedChecklist && !shouldExpireIncompleteChecklist) return;
    if (expiryTrackedRef.current) return;

    expiryTrackedRef.current = true;

    trackOnboardingEvent(
      shouldExpireCompletedChecklist
        ? CI_BASE_ONBOARDING_EVENTS.checklistExpiredComplete
        : CI_BASE_ONBOARDING_EVENTS.checklistExpiredIncomplete
    );

    setLifecycle({
      ...lifecycle,
      hiddenAt: getCurrentTimestamp(),
      hiddenReason: shouldExpireCompletedChecklist ? 'completed-expired' : 'incomplete-expired',
    });
  }, [
    trackLifecycle,
    isLoading,
    isPermanentlyHidden,
    shouldExpireCompletedChecklist,
    shouldExpireIncompleteChecklist,
    lifecycle,
    setLifecycle,
    hasOnboardingStatusError,
  ]);

  const completeTask = useCallback(
    (groupId: string) => {
      const baseTask = groupId as OnboardingBaseTask;

      if (hasOnboardingStatusError) return;

      if (completedGroups.includes(baseTask)) return;

      const isAllCompletedAfterThisTask = flow.tasks.every(
        (task) => task.id === groupId || completedGroups.includes(task.id as OnboardingBaseTask)
      );

      const nextStatus = isAllCompletedAfterThisTask
        ? OnboardingStatus.ONBOARDING_STATUS_COMPLETED
        : OnboardingStatus.ONBOARDING_STATUS_STARTED;

      const completedTask = flow.tasks.find((task) => task.id === groupId);

      pendingTaskCompletionMetadataRef.current.set(baseTask, {
        taskId: groupId,
        taskLabel: completedTask?.label,
        completedEvent: completedTask?.tracking?.completedEvent,
        isAllCompletedAfterThisTask,
      });

      updateStatus({
        productTier: ProductTier.PRODUCT_TIER_BASE,
        status: nextStatus,
        task: {
          baseTask,
          status: TaskStatus.TASK_STATUS_COMPLETED,
        },
      });
    },
    [completedGroups, flow.tasks, hasOnboardingStatusError, updateStatus]
  );

  return {
    completedGroups,
    onboardingStatus,
    isLoading,
    hasOnboardingStatusError,
    completeTask,
    flow,
    isPermanentlyHidden,
    shouldExpireIncompleteChecklist,
    shouldExpireCompletedChecklist,
    incompleteExpiryAnchor,
  };
}
